<script setup lang="ts">
import { useAsyncState } from '@vueuse/core';
import { computed } from 'vue';
import {
  CustomerErhlLoanPurposeOptions,
  ErhlEquityAccessMethod,
  ErhlLoanPurpose,
  depositTypeOptions,
  erhlPropertyTypeOptions,
  stampDutyStatusOptions,
} from '@/helpers/const';
import type { ErhlLoanPurposeValue } from '@/helpers/const';
import { formatAddress, formatCurrency, formatDate, possessive } from '@/helpers/format';
import { useCalcApi } from '@/helpers/useCalcApi';
import { useDeclarationProcess } from '@/helpers/useDeclarationProcess';
import type { ApplicantId, IncomingProperty, PropertyOwnership } from '@/types';
import ApplicantSummary from './ApplicantSummary.vue';

const { loanApp } = useDeclarationProcess();
const calc = useCalcApi();

const purchasePurposes: ErhlLoanPurposeValue[] = [
  ErhlLoanPurpose.PURCHASE_NEW_PROPERTY,
  ErhlLoanPurpose.PURCHASE_NON_SECURITY_PROPERTY,
];

const isPurchase = computed(() =>
  (loanApp.value.erhlLoanPurposes ?? []).some((purpose) => purchasePurposes.includes(purpose)),
);

const purposeLabels = computed(() =>
  (loanApp.value.erhlLoanPurposes ?? []).map((purpose) => CustomerErhlLoanPurposeOptions[purpose]).filter(Boolean),
);

const lumpSum = computed(() => loanApp.value.erhlDrawdowns?.find((drawdown) => drawdown.drawdownType === 'lump_sum'));

const scheduledRelease = computed(() =>
  loanApp.value.erhlDrawdowns?.find((drawdown) => drawdown.drawdownType === 'scheduled_equity_release'),
);

const showScheduledRelease = computed(
  () =>
    loanApp.value.erhlEquityAccessMethod === ErhlEquityAccessMethod.LUMP_SUM_AND_MONTHLY_DRAWDOWN ||
    (scheduledRelease.value?.amount ?? 0) > 0,
);

const additionalBorrowingAmount = computed(() =>
  isPurchase.value ? loanApp.value.additionalFundsRequired?.fundsRequiredAmount ?? 0 : 0,
);

const additionalContribution = computed(() =>
  (loanApp.value.incomingProperties ?? []).reduce((total, property) => total + (property.erhlSavingsAmount ?? 0), 0),
);

const scheduledDurationYears = computed(() => Math.round((scheduledRelease.value?.durationTotalMonths ?? 0) / 12));

const scheduledPaymentCount = computed(() => Math.max((scheduledRelease.value?.durationTotalMonths ?? 0) - 1, 0));

const { state: fundsRequiredForPurchase, isReady: isFundsRequiredReady } = useAsyncState(
  () => (isPurchase.value ? calc.getLoanAmount(loanApp.value) : Promise.resolve(0)),
  0,
);

const { state: totalScheduledEquityRelease, isReady: isScheduledReady } = useAsyncState(
  () =>
    showScheduledRelease.value && scheduledRelease.value
      ? calc.getTotalScheduledEquityRelease(loanApp.value, {
          amount: scheduledRelease.value.amount ?? 0,
          durationTotalMonths: scheduledRelease.value.durationTotalMonths ?? 0,
        })
      : Promise.resolve(0),
  0,
);

const { state: settlementFeeAmount, isReady: isSettlementFeeReady } = useAsyncState(
  () => (isPurchase.value ? Promise.resolve(0) : calc.getSettlementFee(loanApp.value)),
  0,
);

const { state: setupFeeRate, isReady: isSetupFeeRateReady } = useAsyncState(
  () => (isPurchase.value ? Promise.resolve(0) : calc.getSetupFeeRate(loanApp.value)),
  0,
);

const { state: mortgagePayoutEstimate, isReady: isMortgagePayoutReady } = useAsyncState(
  () => (isPurchase.value ? Promise.resolve(0) : calc.getMortgagePayoutEstimate(loanApp.value)),
  0,
);

const isTotalReady = computed(() => {
  const scheduledReady = !showScheduledRelease.value || isScheduledReady.value;
  if (isPurchase.value) return isFundsRequiredReady.value && scheduledReady;
  return isSettlementFeeReady.value && isSetupFeeRateReady.value && isMortgagePayoutReady.value && scheduledReady;
});

/**
 * Estimated principal balance at the end of the loan term, using the same calculation
 * service as the Equity Release loan details page.
 */
const estimatedPrincipalBalance = computed(() => {
  if (isPurchase.value) {
    return fundsRequiredForPurchase.value + additionalBorrowingAmount.value + totalScheduledEquityRelease.value;
  }

  const rate = setupFeeRate.value ?? 0;
  const base =
    (lumpSum.value?.amount ?? 0) +
    totalScheduledEquityRelease.value +
    mortgagePayoutEstimate.value +
    settlementFeeAmount.value;
  const grossed = rate > 0 && rate < 1 ? base + base * (rate / (1 - rate)) : base;
  return Math.ceil(grossed / 1000) * 1000;
});

const propertyAddresses = computed(() => {
  const addresses: Record<string, string> = {};
  const properties = [
    ...loanApp.value.incomingProperties,
    ...loanApp.value.outgoingProperties,
    ...(loanApp.value.supportingProperties ?? []),
  ];
  for (const property of properties) {
    addresses[property.id] = formatAddress(property.address);
  }
  return addresses;
});

const applicantsById = computed(() =>
  loanApp.value.applicants.reduce((lookup, applicant) => {
    lookup[applicant.id] = applicant;
    return lookup;
  }, {} as Record<ApplicantId, (typeof loanApp.value.applicants)[number]>),
);

const ownershipRows = computed(() =>
  loanApp.value.propertiesOwnership.filter((ownership) => ownership.type !== 'asset'),
);

/**
 * Purchase or estimated purchase date shown on the buying summary.
 */
const purchaseDate = (property: IncomingProperty) => {
  const date = property.settlementDate || loanApp.value.settlementDate;
  return date ? formatDate(date) : '';
};

/**
 * Customer-facing property type label from the Equity Release list.
 */
const propertyTypeLabel = (propertyType?: string) => {
  if (!propertyType) return '';
  const labels: Record<string, string> = erhlPropertyTypeOptions;
  return labels[propertyType] ?? '';
};

/**
 * Section heading for a property ownership row.
 */
const ownershipHeading = (ownership: PropertyOwnership) => {
  const address = propertyAddresses.value[ownership.id] ?? '';
  switch (ownership.type) {
    case 'incoming':
      return `Buying - ${address}`;
    case 'supporting':
      return `Supporting - ${address}`;
    default:
      return `Selling - ${address}`;
  }
};

/**
 * Share label for one applicant on a property.
 */
const shareLabel = (applicantId: string) => {
  const applicant = applicantsById.value[applicantId as ApplicantId];
  if (!applicant) return '';
  return `${possessive(applicant.preferredName || applicant.firstName)} share`;
};
</script>

<template>
  <div>
    <section class="summary-details">
      <h2>Loan purpose</h2>
      <h3>Loan type</h3>
      <dl>
        <dt>Loan product</dt>
        <dd>Equity Release Home Loan</dd>
        <template v-if="purposeLabels.length">
          <dt>Your loan purpose</dt>
          <dd>
            <div v-for="label in purposeLabels" :key="label">{{ label }}</div>
          </dd>
        </template>
      </dl>
    </section>

    <section v-if="isPurchase && loanApp.incomingProperties.length" class="summary-details">
      <h2>Buying</h2>
      <template v-for="(property, index) in loanApp.incomingProperties" :key="property.id">
        <h3>Property{{ loanApp.incomingProperties.length > 1 ? ` ${index + 1}` : '' }} - Information</h3>
        <dl>
          <dt>Type</dt>
          <dd>{{ propertyTypeLabel(property.propertyType) }}</dd>
          <dt>Do you have a contract for sale?</dt>
          <dd>{{ property.contractForSale ? 'Yes' : 'No' }}</dd>
          <dt>Address</dt>
          <dd>{{ formatAddress(property.address) }}</dd>
        </dl>
        <h3>Property{{ loanApp.incomingProperties.length > 1 ? ` ${index + 1}` : '' }} - Deposit</h3>
        <dl>
          <dt>{{ property.contractForSale ? 'Deposit' : 'Estimated deposit' }} amount</dt>
          <dd>{{ formatCurrency(property.depositAmount) }}</dd>
          <dt>{{ property.contractForSale ? 'Purchase' : 'Estimated purchase' }} price</dt>
          <dd>{{ formatCurrency(property.purchaseAmount) }}</dd>
          <dt>{{ property.contractForSale ? 'Purchase' : 'Estimated purchase' }} date</dt>
          <dd>{{ purchaseDate(property) }}</dd>
          <dt>{{ property.contractForSale ? 'How was the deposit paid?' : 'How will you pay your deposit?' }}</dt>
          <dd>{{ property.depositType && depositTypeOptions[property.depositType] }}</dd>
          <dt>How will stamp duty be paid?</dt>
          <dd>{{ property.stampDutyStatus && stampDutyStatusOptions[property.stampDutyStatus] }}</dd>
        </dl>
      </template>
    </section>

    <section v-if="loanApp.supportingProperties?.length" class="summary-details">
      <h2>Supporting</h2>
      <template v-for="(property, index) in loanApp.supportingProperties" :key="property.id">
        <h3>Property {{ index + 1 }} - Information</h3>
        <dl>
          <dt>Type</dt>
          <dd>{{ propertyTypeLabel(property.propertyType) }}</dd>
          <dt>Address</dt>
          <dd>{{ formatAddress(property.address) }}</dd>
        </dl>
        <h3>Property {{ index + 1 }} - Mortgage</h3>
        <dl>
          <dt>Is there a mortgage against this property?</dt>
          <dd>{{ property.outstandingLoanAmount > 0 ? 'Yes' : 'No' }}</dd>
          <template v-if="property.outstandingLoanAmount > 0">
            <dt>Outstanding mortgage</dt>
            <dd>{{ formatCurrency(property.outstandingLoanAmount) }}</dd>
          </template>
        </dl>
        <h3>Property {{ index + 1 }} - Details</h3>
        <dl>
          <dt>Estimated property value</dt>
          <dd>{{ formatCurrency(property.supportingPropertyValue) }}</dd>
        </dl>
      </template>
    </section>

    <section class="summary-details">
      <h2>Loan details</h2>
      <template v-if="isPurchase">
        <h3>Lump sum</h3>
        <dl>
          <dt>Funds required for purchase</dt>
          <dd>
            <template v-if="isFundsRequiredReady">{{ formatCurrency(fundsRequiredForPurchase) }}</template>
            <BxSpinner v-else />
          </dd>
          <template v-if="additionalBorrowingAmount > 0">
            <dt>Additional borrowing amount</dt>
            <dd>{{ formatCurrency(additionalBorrowingAmount) }}</dd>
          </template>
          <template v-if="additionalContribution > 0">
            <dt>Additional funds to contribute to purchase</dt>
            <dd>{{ formatCurrency(additionalContribution) }}</dd>
          </template>
        </dl>
      </template>
      <template v-else>
        <h3>Lump sum</h3>
        <dl>
          <dt>Initial lump sum amount</dt>
          <dd>{{ formatCurrency(lumpSum?.amount ?? 0) }}</dd>
          <dt>Funds required by</dt>
          <dd>{{ lumpSum?.fundsRequiredBy ? formatDate(lumpSum.fundsRequiredBy) : '' }}</dd>
        </dl>
      </template>

      <template v-if="showScheduledRelease">
        <h3>Scheduled equity release</h3>
        <dl>
          <dt>Amount</dt>
          <dd>{{ formatCurrency(scheduledRelease?.amount ?? 0) }} per month</dd>
          <dt>Duration</dt>
          <dd>{{ scheduledDurationYears }} {{ scheduledDurationYears === 1 ? 'year' : 'years' }}</dd>
          <dt>Total scheduled equity release</dt>
          <dd>
            <template v-if="isScheduledReady">
              {{ formatCurrency(totalScheduledEquityRelease) }} ({{ scheduledPaymentCount }} monthly payments)
            </template>
            <BxSpinner v-else />
          </dd>
        </dl>
      </template>

      <h3>Total loan amount</h3>
      <dl>
        <dt>Estimated principal balance at end of loan term*</dt>
        <dd>
          <template v-if="isTotalReady">{{ formatCurrency(estimatedPrincipalBalance) }}</template>
          <BxSpinner v-else />
        </dd>
      </dl>
      <p class="text-sm text-gray-500">*Includes existing mortgage payout (if any) and applicable fees and charges.</p>
    </section>

    <ApplicantSummary :data="loanApp.applicants" />

    <section v-if="ownershipRows.length" class="summary-details">
      <h2>Property ownership</h2>
      <template v-for="ownership in ownershipRows" :key="ownership.id">
        <h3>{{ ownershipHeading(ownership) }}</h3>
        <dl>
          <template v-for="(share, applicantId) in ownership.share" :key="applicantId">
            <template v-if="shareLabel(String(applicantId))">
              <dt>{{ shareLabel(String(applicantId)) }}</dt>
              <dd>{{ share }}%</dd>
            </template>
          </template>
        </dl>
      </template>
    </section>
  </div>
</template>
